"use client";

import { c as _c } from "react/compiler-runtime";
import type { IconType } from "react-icons";
import { FiCalendar, FiClock, FiSlash, FiUsers } from "react-icons/fi";
import { Button } from "@/components/ui";
import { useBookingEvent } from "@/hooks/self-schedule/use-booking-event";
import { formatSession } from "@/lib/booking-event-sessions";
import { formatDate, formatTime } from "@/lib/format-data";
import { useStoreTimezone } from "@/lib/timezone";
import { BookingEventStatus } from "@/types/booking-event";
import { useSelfScheduleStore } from "@/stores/self-schedule-store";
const COPY: Record<Exclude<BookingEventStatus, "OPEN">, {
  icon: IconType;
  headline: string;
  body: string;
}> = {
  NOT_YET_OPEN: {
    icon: FiClock,
    headline: "Booking hasn't opened yet",
    body: "Check back when booking opens to pick your time."
  },
  CLOSED: {
    icon: FiSlash,
    headline: "Booking for this event has closed",
    body: "This event isn't taking new bookings online."
  },
  FULL: {
    icon: FiUsers,
    headline: "This event is fully booked",
    body: "Every spot has been taken."
  },
  ENDED: {
    icon: FiCalendar,
    headline: "This event has ended",
    body: "Thanks for your interest."
  }
};

/**
 * Stands in for the booking steps when an event link isn't taking bookings,
 * so the patient learns why instead of landing in ordinary booking unawares.
 * They can still choose to book a regular appointment.
 */
export function BookingEventGate(t0) {
  const $ = _c(34);
  const {
    children
  } = t0;
  const {
    data: bookingEvent
  } = useBookingEvent();
  const setBookingEventId = useSelfScheduleStore(_temp);
  const selectedProviderId = useSelfScheduleStore(_temp2);
  const setSelectedProviderId = useSelfScheduleStore(_temp3);
  const timeZone = useStoreTimezone();
  if (!bookingEvent || bookingEvent.status === "OPEN") {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const {
    icon: Icon,
    headline,
    body
  } = COPY[bookingEvent.status];
  let t1;
  if ($[2] !== bookingEvent.bookingOpensAt || $[3] !== bookingEvent.status || $[4] !== timeZone) {
    t1 = bookingEvent.status === "NOT_YET_OPEN" && bookingEvent.bookingOpensAt ? `Booking opens ${formatDate(bookingEvent.bookingOpensAt, {
      weekday: "long",
      month: "long",
      day: "numeric",
      timeZone
    })} at ${formatTime(bookingEvent.bookingOpensAt, true, timeZone)}.` : null;
    $[2] = bookingEvent.bookingOpensAt;
    $[3] = bookingEvent.status;
    $[4] = timeZone;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const opensAt = t1;
  const showSessions = bookingEvent.status !== "ENDED";
  let t2;
  if ($[6] !== Icon) {
    t2 = <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-background-secondary"><Icon className="size-5 text-foreground-secondary" /></div>;
    $[6] = Icon;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== bookingEvent.name) {
    t3 = <p className="text-overline text-foreground-tertiary">{bookingEvent.name}</p>;
    $[8] = bookingEvent.name;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== headline) {
    t4 = <h1 className="text-h2 text-foreground">{headline}</h1>;
    $[10] = headline;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = opensAt ?? body;
  let t6;
  if ($[12] !== t5) {
    t6 = <p className="text-body text-foreground-secondary">{t5}</p>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t4 || $[16] !== t6) {
    t7 = <div className="space-y-2">{t3}{t4}{t6}</div>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== bookingEvent.sessions || $[19] !== showSessions) {
    t8 = showSessions && bookingEvent.sessions.length > 0 && <ul className="space-y-1 text-body text-foreground">{bookingEvent.sessions.map(_temp4)}</ul>;
    $[18] = bookingEvent.sessions;
    $[19] = showSessions;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== bookingEvent.providerId || $[22] !== selectedProviderId || $[23] !== setBookingEventId || $[24] !== setSelectedProviderId) {
    t9 = <Button className="w-full sm:w-auto" onClick={() => {
      if (bookingEvent.providerId && selectedProviderId === bookingEvent.providerId) {
        setSelectedProviderId(null);
      }
      setBookingEventId(null);
    }}>Book a regular appointment</Button>;
    $[21] = bookingEvent.providerId;
    $[22] = selectedProviderId;
    $[23] = setBookingEventId;
    $[24] = setSelectedProviderId;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <p className="text-caption text-foreground-tertiary">Regular appointments are at regular prices.</p>;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t9) {
    t11 = <div className="space-y-2">{t9}{t10}</div>;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t11 || $[30] !== t2 || $[31] !== t7 || $[32] !== t8) {
    t12 = <div className="flex min-h-full items-center justify-center px-4 py-12"><div className="w-full max-w-md space-y-6 text-center">{t2}{t7}{t8}{t11}</div></div>;
    $[29] = t11;
    $[30] = t2;
    $[31] = t7;
    $[32] = t8;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}
function _temp4(session) {
  return <li key={`${session.day}-${session.startMinute}`}>{formatSession(session)}</li>;
}
function _temp3(state_1) {
  return state_1.setSelectedProviderId;
}
function _temp2(state_0) {
  return state_0.selectedProviderId;
}
function _temp(state) {
  return state.setBookingEventId;
}